import { useCallback, useEffect, useMemo, useState } from "react";
import { Bot, Cable, Copy, Download, Eye, EyeOff, Gamepad2, KeyRound, RefreshCw, Send, Server, Upload, WalletCards } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const attachmentEmpty = {
  gameId: "",
  name: "",
  username: "",
  password: "",
  apiBaseUrl: "",
  authorizationToken: "",
  verificationCode: "",
  verificationNonce: "",
  googleCode: "",
  testConnection: true,
};

const playerEmpty = {
  gameName: "",
  username: "",
  password: "",
  amount: "1000",
  remark: "",
};

const isMaskedSecret = (value) => {
  const clean = String(value || "").trim();
  if (!clean) return false;
  if (["[redacted]", "redacted", "[hidden]", "hidden", "masked"].includes(clean.toLowerCase())) return true;
  return clean.length >= 6 && /^[*•●xX]+$/.test(clean);
};

const cleanOptionalSecret = (value) => {
  const clean = String(value || "").trim();
  return clean && !isMaskedSecret(clean) ? clean : undefined;
};

function attachmentForGame(attachments, gameId) {
  const target = automationGameId(gameId);
  const matches = attachments.filter((item) => automationGameId(gameIdFromAutomationRow(item)) === target);
  return matches.find((item) => item.source === "direct-provider") || matches[0] || null;
}

function defaultApiBaseUrlForGame(game = {}) {
  if (game.adapterKind === "river-office") return "https://river-pay.com";
  return "";
}

function attachmentDraftForGame(gameId, attachments, gameOptions) {
  const game = gameOptions.find((item) => item.id === gameId);
  const existing = attachmentForGame(attachments, gameId);
  const label = game?.label || existing?.displayName || existing?.name || gameId;
  return {
    ...attachmentEmpty,
    gameId,
    name: existing?.displayName || existing?.name || `${label} Backend`,
    username: existing?.username || "",
    apiBaseUrl: existing?.apiBaseUrl || existing?.api_base_url || defaultApiBaseUrlForGame(game),
  };
}

export default function AdminAutomation() {
  const queryGameId = useMemo(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(window.location.search);
    return automationGameId(params.get("gameId") || params.get("gameName") || "");
  }, []);
  const [localGames, setLocalGames] = useState([]);
  const [automationGames, setAutomationGames] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [backendLogins, setBackendLogins] = useState([]);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [attach, setAttach] = useState(attachmentEmpty);
  const [player, setPlayer] = useState(playerEmpty);
  const [captcha, setCaptcha] = useState(null);
  const [busy, setBusy] = useState("");
  const [showBackendLogins, setShowBackendLogins] = useState(true);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const [localGameRes, autoGameRes, attachRes, loginRes, accountRes, logRes] = await Promise.allSettled([
      api.get("/games"),
      api.get("/admin/automation/games"),
      api.get("/admin/automation/backend-attachments"),
      api.get("/admin/automation/backend-login-list"),
      api.get("/admin/automation/provider-accounts"),
      api.get("/admin/automation/logs?limit=20"),
    ]);
    if (localGameRes.status === "fulfilled") setLocalGames(toRows(localGameRes.value.data));
    if (autoGameRes.status === "fulfilled") setAutomationGames(toRows(autoGameRes.value.data));
    if (attachRes.status === "fulfilled") setAttachments(toRows(attachRes.value.data));
    if (loginRes.status === "fulfilled") setBackendLogins(toRows(loginRes.value.data));
    if (accountRes.status === "fulfilled") setProviderAccounts(toRows(accountRes.value.data));
    if (logRes.status === "fulfilled") setLogs(toRows(logRes.value.data));
  }, []);

  useEffect(() => {
    load().catch((err) => toast.error(formatApiError(err)));
  }, [load]);

  const supportedGameIds = useMemo(() => {
    const ids = new Set();
    automationGames.forEach((game) => {
      const id = gameIdFromAutomationRow(game);
      if (id) ids.add(id);
    });
    attachments.forEach((item) => {
      const id = gameIdFromAutomationRow(item);
      if (id) ids.add(id);
    });
    return ids;
  }, [attachments, automationGames]);

  const attachedGameIds = useMemo(() => {
    const ids = new Set();
    attachments.forEach((item) => {
      const id = gameIdFromAutomationRow(item);
      if (id) ids.add(id);
    });
    return ids;
  }, [attachments]);

  const gameOptions = useMemo(() => {
    const rows = new Map();
    const add = (id, label, source, meta = {}) => {
      if (!id) return;
      const key = String(id);
      const next = { id: key, label: label || key, source, ...meta };
      if (!rows.has(key)) rows.set(key, next);
      else if (source !== "local") rows.set(key, { ...rows.get(key), ...next, label: label || rows.get(key).label, source });
    };
    localGames.forEach((game) => add(automationGameId(game.name || game.title || game.id), game.name || game.title, "local"));
    automationGames.forEach((game) => add(gameIdFromAutomationRow(game), game.displayName || game.name || game.title, "automation", { adapterKind: game.adapterKind, requiresCaptcha: game.requiresCaptcha, clientCaptchaOnly: game.clientCaptchaOnly }));
    attachments.forEach((item) => add(gameIdFromAutomationRow(item), item.displayName || item.name || item.title, "attached", { adapterKind: item.adapterKind, requiresCaptcha: item.requiresCaptcha, clientCaptchaOnly: item.clientCaptchaOnly }));
    return [...rows.values()]
      .map((game) => ({
        ...game,
        supported: supportedGameIds.has(game.id),
        attached: attachedGameIds.has(game.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [attachedGameIds, attachments, automationGames, localGames, supportedGameIds]);

  const supportedGameOptions = useMemo(() => (
    gameOptions.filter((game) => game.supported)
  ), [gameOptions]);

  useEffect(() => {
    const queryGameReady = queryGameId && gameOptions.some((game) => game.id === queryGameId);
    const preferredGame = queryGameReady ? queryGameId : supportedGameOptions[0]?.id || gameOptions[0]?.id || "";
    if (!preferredGame) return;
    const existingAttachment = attachmentForGame(attachments, preferredGame);
    setAttach((current) => {
      if (!queryGameReady && current.gameId && gameOptions.some((game) => game.id === current.gameId)) return current;
      if (current.gameId === preferredGame) {
        if (queryGameReady && existingAttachment && (!current.username || !current.name || !current.apiBaseUrl)) {
          const draft = attachmentDraftForGame(preferredGame, attachments, gameOptions);
          return {
            ...current,
            name: current.name || draft.name,
            username: current.username || draft.username,
            apiBaseUrl: current.apiBaseUrl || draft.apiBaseUrl,
          };
        }
        return current;
      }
      return attachmentDraftForGame(preferredGame, attachments, gameOptions);
    });
    setPlayer((current) => {
      if (queryGameReady) return { ...current, gameName: preferredGame };
      return current.gameName && gameOptions.some((game) => game.id === current.gameName) ? current : { ...current, gameName: preferredGame };
    });
  }, [attachments, gameOptions, queryGameId, supportedGameOptions]);

  const supportedList = supportedGameOptions.map((game) => game.label).join(", ");
  const selectedPlayerAttached = !player.gameName || attachedGameIds.has(player.gameName);
  const selectedAttachGame = gameOptions.find((game) => game.id === attach.gameId);
  const selectedExistingAttachment = attachmentForGame(attachments, attach.gameId);
  const selectedAttachKind = selectedAttachGame?.adapterKind || "";
  const selectedAttachRequiresCaptcha = Boolean(selectedAttachGame?.requiresCaptcha);
  const selectedAttachClientCaptchaOnly = Boolean(selectedAttachGame?.clientCaptchaOnly);
  const selectedAttachUsesApiBaseUrl = selectedAttachKind === "external-api" || selectedAttachKind === "aspx-store" || selectedAttachKind === "river-office";
  const selectedAttachNeedsCaptcha = selectedAttachKind === "management-api" || selectedAttachRequiresCaptcha;
  const passwordRequired = selectedAttachKind !== "external-api" && !selectedExistingAttachment?.hasPassword;

  const submitAttachment = async (e) => {
    e.preventDefault();
    if (!supportedGameIds.has(attach.gameId)) {
      toast.error(`${attach.gameId} is not enabled in the automation group yet`);
      setResult({ title: "Unsupported game", data: { gameId: attach.gameId, availableGames: supportedGameOptions } });
      return;
    }
    setBusy("attach");
    try {
      const payload = {
        gameId: attach.gameId,
        name: attach.name,
        username: attach.username,
        password: cleanOptionalSecret(attach.password) || "",
        apiBaseUrl: attach.apiBaseUrl || undefined,
        authorizationToken: cleanOptionalSecret(attach.authorizationToken),
        verificationCode: attach.verificationCode || undefined,
        verificationNonce: attach.verificationNonce || undefined,
        googleCode: attach.googleCode || undefined,
        testConnection: selectedAttachClientCaptchaOnly ? false : Boolean(attach.testConnection),
      };
      const { data } = await api.post("/admin/automation/backend-attachments", payload);
      setResult({ title: "Backend attached", data });
      if (data?.saved_without_live_test) {
        toast.warning(data.warning || "Backend saved without live test");
      } else {
        toast.success("Backend attachment saved");
      }
      setAttach(attachmentEmpty);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      setResult({ title: "Attachment failed", data: err?.response?.data || formatApiError(err) });
    } finally {
      setBusy("");
    }
  };

  const loadCaptcha = async () => {
    if (!attach.gameId) {
      toast.error("Select a game first");
      return;
    }
    setBusy("captcha");
    try {
      const { data } = await api.get(`/admin/automation/captcha?gameName=${encodeURIComponent(attach.gameId)}`);
      setCaptcha(data);
      setAttach((current) => ({ ...current, verificationNonce: data.nonce || "" }));
      toast.success("Captcha loaded");
    } catch (err) {
      toast.error(formatApiError(err));
      setResult({ title: "Captcha failed", data: err?.response?.data || formatApiError(err) });
    } finally {
      setBusy("");
    }
  };

  const runPlayerAction = async (action) => {
    if (!player.gameName) {
      toast.error("Select a game first");
      return;
    }
    if (["load", "redeem"].includes(action) && Number(player.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!supportedGameIds.has(player.gameName)) {
      toast.error(`${player.gameName} is not enabled in the automation group yet`);
      setResult({ title: "Unsupported game", data: { gameName: player.gameName, availableGames: supportedGameOptions } });
      return;
    }
    if (!attachedGameIds.has(player.gameName)) {
      toast.error("Attach this game backend first");
      setResult({ title: "Backend missing", data: { gameName: player.gameName, attachedGames: [...attachedGameIds] } });
      return;
    }
    setBusy(action);
    try {
      const payload = {
        gameName: player.gameName,
        username: player.username || undefined,
        password: player.password || undefined,
        remark: player.remark || undefined,
      };
      if (["load", "redeem"].includes(action)) payload.amount = Number(player.amount);
      const { data } = await api.post(`/admin/automation/${action}`, payload);
      setResult({ title: action.replace("-", " "), data });
      toast.success(`${action.replace("-", " ")} complete`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      setResult({ title: `${action.replace("-", " ")} failed`, data: err?.response?.data || formatApiError(err) });
    } finally {
      setBusy("");
    }
  };

  const runGameBalance = async () => {
    if (!player.gameName) {
      toast.error("Select a game first");
      return;
    }
    if (!attachedGameIds.has(player.gameName)) {
      toast.error("Attach this game backend first");
      setResult({ title: "Backend missing", data: { gameName: player.gameName, attachedGames: [...attachedGameIds] } });
      return;
    }
    setBusy("game-balance");
    try {
      const { data } = await api.post("/admin/automation/game-balance", { gameName: player.gameName });
      setResult({ title: "game balance", data });
      toast.success("Game balance fetched");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      setResult({ title: "game balance failed", data: err?.response?.data || formatApiError(err) });
    } finally {
      setBusy("");
    }
  };

  const copyBackendLogins = async () => {
    if (!backendLogins.length) {
      toast.error("Backend login list is empty");
      return;
    }
    const text = formatBackendLoginList(backendLogins);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Backend login list copied");
    } catch {
      setResult({ title: "Backend login list", data: text });
      toast.warning("Copy blocked. Login list shown in Last result.");
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Backend Attachments</h1>
          <p className="mt-1 text-sm text-slate-500">Provider automation runs server-to-server. The browser never receives the group token.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowBackendLogins((current) => !current)}
            className="inline-flex items-center gap-2 rounded-md border border-[#2F6FA5]/40 px-3 py-2 text-sm font-semibold text-[#8cc7ff] hover:bg-[#2F6FA5]/10"
          >
            {showBackendLogins ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showBackendLogins ? "Hide agent logins" : "Show agent logins"}
          </button>
          <button
            type="button"
            disabled={!backendLogins.length}
            onClick={copyBackendLogins}
            className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-50"
            title="Copy game-wise backend login list"
          >
            <Copy className="h-4 w-4" />
            Copy list
          </button>
          <button onClick={() => load().catch((err) => toast.error(formatApiError(err)))} className="rounded-md border border-white/10 p-2 hover:bg-white/5" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-5">
        <Stat icon={Gamepad2} label="Local games" value={localGames.length} />
        <Stat icon={Gamepad2} label="Automation games" value={automationGames.length} />
        <Stat icon={Server} label="Attachments" value={attachments.length} />
        <Stat icon={KeyRound} label="Provider accounts" value={providerAccounts.length} />
        <Stat icon={Bot} label="Recent logs" value={logs.length} />
      </div>

      <BackendCredentialsPanel rows={backendLogins} visible={showBackendLogins} onCopyAll={copyBackendLogins} />

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <form onSubmit={submitAttachment} className="rounded-xl border border-white/10 bg-[#121214] p-5">
          <div className="mb-5 flex items-center gap-2">
            <Cable className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold">Attach Provider Backend</h2>
          </div>
          <div className="mb-4 rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#f5d76e]">
            Automation group feabby currently supports: {supportedList || "loading games..."}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Game">
              <select
                required
                value={attach.gameId}
                onChange={(e) => {
                  const gameId = e.target.value;
                  setCaptcha(null);
                  setAttach(gameId ? attachmentDraftForGame(gameId, attachments, gameOptions) : attachmentEmpty);
                }}
                className={inputClass}
              >
                <option value="">Select game</option>
                {gameOptions.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.label} ({game.id}) {game.attached ? "- attached" : game.supported ? "- ready" : "- not enabled"}
                  </option>
                ))}
              </select>
              {attach.gameId && !supportedGameIds.has(attach.gameId) && (
                <p className="mt-2 text-xs text-amber-300">This game is still visible, but it needs a provider/API connection before it can be attached.</p>
              )}
            </Field>
            <Field label="Attachment name">
              <input required value={attach.name} onChange={(e) => setAttach({ ...attach, name: e.target.value })} className={inputClass} placeholder="My Las Vegas Sweeps Backend" />
            </Field>
            <Field label={selectedAttachKind === "external-api" ? "Agent ID" : "Backend username"}>
              <input required value={attach.username} onChange={(e) => setAttach({ ...attach, username: e.target.value })} className={inputClass} placeholder={selectedAttachKind === "external-api" ? "agent_id" : "Backend username"} />
            </Field>
            <Field label="Backend password">
              <input required={passwordRequired} type="password" value={attach.password} onChange={(e) => setAttach({ ...attach, password: e.target.value })} className={inputClass} autoComplete="new-password" placeholder={selectedAttachKind === "external-api" ? "Optional fallback secret" : "Backend password"} />
              <p className="mt-2 text-xs text-slate-500">Leave blank to keep the saved password.</p>
            </Field>
            {selectedAttachKind === "external-api" && (
              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#f5d76e] md:col-span-2">
                API setup: put provider agent_id in Agent ID and the API secret key below.
              </div>
            )}
            {selectedAttachKind === "aspx-store" && selectedAttachRequiresCaptcha && (
              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#f5d76e] md:col-span-2">
                Store setup: use the provider portal base URL, load the captcha below, type the code, then attach. The backend logs in from the server and saves the session automatically.
              </div>
            )}
            {selectedAttachKind === "aspx-store" && !selectedAttachRequiresCaptcha && (
              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#f5d76e] md:col-span-2">
                Store setup: use the provider admin base URL and paste a fresh logged-in Cookie in Provider Cookie / session token. This provider does not expose a server captcha login page.
              </div>
            )}
            {selectedAttachKind === "river-office" && (
              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#f5d76e] md:col-span-2">
                River setup: paste a fresh logged-in provider Cookie in the session token field. River uses Google reCAPTCHA, so the backend cannot create a fresh login without a browser session.
              </div>
            )}
            {selectedAttachUsesApiBaseUrl && (
              <Field label="API base URL" col>
                <input value={attach.apiBaseUrl} onChange={(e) => setAttach({ ...attach, apiBaseUrl: e.target.value })} className={inputClass} placeholder={selectedAttachKind === "aspx-store" ? "https://orionstars.vip:8781" : selectedAttachKind === "river-office" ? "https://river-pay.com" : "https://provider-domain.com/api/external or https://provider-domain.com"} />
                <p className="mt-2 text-xs text-slate-500">Use the provider API domain from your docs/account. Leave blank only if the default provider URL is correct.</p>
              </Field>
            )}
            {selectedAttachNeedsCaptcha && (
              <>
                <Field label="Provider captcha" col>
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-black/20 p-3">
                    <button type="button" disabled={busy === "captcha"} onClick={loadCaptcha} className="inline-flex items-center gap-2 rounded-md border border-[#D4AF37]/40 px-3 py-2 text-sm font-semibold text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50">
                      <RefreshCw className="h-4 w-4" />
                      {busy === "captcha" ? "Loading..." : "Load captcha"}
                    </button>
                    {captcha?.imageUrl && (
                      <img
                        src={captcha.imageUrl}
                        alt="Provider captcha"
                        className="h-12 rounded border border-white/10 bg-white object-contain px-2"
                      />
                    )}
                    {attach.verificationNonce && <span className="text-xs text-slate-500">Nonce saved for next attach test.</span>}
                  </div>
                </Field>
                <Field label="Verification code (optional)">
                  <input value={attach.verificationCode} onChange={(e) => setAttach({ ...attach, verificationCode: e.target.value })} className={inputClass} autoComplete="one-time-code" placeholder="Captcha / provider code" />
                  <p className="mt-2 text-xs text-slate-500">Used once during connection test. It is not stored.</p>
                </Field>
                {selectedAttachKind === "management-api" && (
                  <Field label="Google / 2FA code (optional)">
                    <input value={attach.googleCode} onChange={(e) => setAttach({ ...attach, googleCode: e.target.value })} className={inputClass} autoComplete="one-time-code" placeholder="Authenticator code" />
                    <p className="mt-2 text-xs text-slate-500">Use when the provider asks for Google verification.</p>
                  </Field>
                )}
              </>
            )}
            <Field label={selectedAttachKind === "external-api" ? "API secret key" : selectedAttachKind === "aspx-store" || selectedAttachKind === "river-office" ? "Provider Cookie / session token" : "Authorization token (optional)"} col>
              <input type="password" value={attach.authorizationToken} onChange={(e) => setAttach({ ...attach, authorizationToken: e.target.value })} className={inputClass} autoComplete="new-password" placeholder={selectedAttachKind === "external-api" ? "secret_key from provider API docs" : selectedAttachKind === "aspx-store" ? "ASP.NET_SessionId=...; .ASPXAUTH=..." : selectedAttachKind === "river-office" ? "PHPSESSID=...; _cfuvid=..." : ""} />
              <p className="mt-2 text-xs text-slate-500">{selectedAttachKind === "aspx-store" ? (selectedAttachRequiresCaptcha ? "Optional. Leave blank and use captcha login so the backend can save a fresh session." : "Required for this provider unless you already saved a valid cookie. Leave blank only to keep the saved cookie.") : selectedAttachKind === "river-office" ? "Required for River live balance. If you change the River username/password and leave this blank, the old session is cleared." : "Leave blank to keep the saved token or API secret key."}</p>
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={selectedAttachClientCaptchaOnly ? false : attach.testConnection}
                disabled={selectedAttachClientCaptchaOnly}
                onChange={(e) => setAttach({ ...attach, testConnection: e.target.checked })}
              />
              Test connection while attaching
            </label>
            {selectedAttachClientCaptchaOnly ? (
              <p className="text-xs text-slate-500 md:col-span-2">
                Live attach test is disabled for this provider to avoid Cloudflare/lockout loops. Save first, then use Game balance when ready.
              </p>
            ) : selectedAttachKind === "gameroom-api" && (
              <p className="text-xs text-slate-500 md:col-span-2">
                If the provider returns a temporary Cloudflare error, uncheck live test to save the credentials first.
              </p>
            )}
          </div>
          <button disabled={busy === "attach"} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#D4AF37] px-4 py-3 font-bold text-black hover:bg-[#F1C40F] disabled:opacity-50">
            <Send className="h-4 w-4" />
            {busy === "attach" ? "Attaching..." : "Attach backend"}
          </button>
        </form>

        <section className="rounded-xl border border-white/10 bg-[#121214] p-5">
          <div className="mb-5 flex items-center gap-2">
            <WalletCards className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold">Automation Test Console</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Game">
              <select required value={player.gameName} onChange={(e) => setPlayer({ ...player, gameName: e.target.value })} className={inputClass}>
                <option value="">Select game</option>
                {gameOptions.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.label} ({game.id}) {game.attached ? "- attached" : game.supported ? "- needs attach" : "- not in automation"}
                  </option>
                ))}
              </select>
              {!selectedPlayerAttached && <p className="mt-2 text-xs text-amber-300">Attach this game's backend before create account, balance, load, or redeem.</p>}
            </Field>
            <Field label="Player username">
              <input value={player.username} onChange={(e) => setPlayer({ ...player, username: e.target.value })} className={inputClass} placeholder="player123" />
            </Field>
            <Field label="Password (optional)">
              <input type="password" value={player.password} onChange={(e) => setPlayer({ ...player, password: e.target.value })} className={inputClass} />
            </Field>
            <Field label="Amount">
              <input type="number" min="1" value={player.amount} onChange={(e) => setPlayer({ ...player, amount: e.target.value })} className={inputClass} />
            </Field>
            <Field label="Remark" col>
              <input value={player.remark} onChange={(e) => setPlayer({ ...player, remark: e.target.value })} className={inputClass} placeholder="website load" />
            </Field>
          </div>

          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <ActionButton busy={busy} id="create-account" icon={KeyRound} onClick={() => runPlayerAction("create-account")}>Create account</ActionButton>
            <ActionButton busy={busy} id="balance" icon={WalletCards} onClick={() => runPlayerAction("balance")}>Balance</ActionButton>
            <ActionButton busy={busy} id="load" icon={Download} onClick={() => runPlayerAction("load")}>Load</ActionButton>
            <ActionButton busy={busy} id="redeem" icon={Upload} onClick={() => runPlayerAction("redeem")}>Redeem</ActionButton>
            <ActionButton busy={busy} id="game-balance" icon={Gamepad2} onClick={runGameBalance}>Game balance</ActionButton>
          </div>
        </section>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <ListPanel title="Backend attachments" rows={attachments} empty="No automation attachments yet." />
        <ListPanel title="Provider accounts" rows={providerAccounts} empty="No provider accounts saved yet." />
        <ResultPanel result={result} />
      </div>

      <section className="mt-6 rounded-xl border border-white/10 bg-[#121214] p-5">
        <h2 className="mb-4 text-lg font-semibold">Recent automation logs</h2>
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="rounded-lg border border-white/5 bg-black/20 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{log.method} {log.endpoint_url}</div>
                  <div className="text-xs text-slate-500">game {log.gameId || "-"} - user {log.local_username || "-"} - provider {log.provider_user_id || "-"}</div>
                </div>
                <span className={`rounded-full border px-2 py-0.5 text-xs ${log.response?.ok ? "border-emerald-700/30 text-emerald-300" : "border-red-700/30 text-red-300"}`}>
                  {log.response?.status_code || 0}
                </span>
              </div>
            </div>
          ))}
          {logs.length === 0 && <div className="py-8 text-center text-sm text-slate-500">No automation logs yet.</div>}
        </div>
      </section>
    </div>
  );
}

function GameCreditCard({ row, busy, onRefresh }) {
  const gameId = gameIdFromAutomationRow(row);
  const isBusy = busy === `game-credit-${gameId}`;
  const ok = row.ok !== false;
  const hasBalance = row.backend_balance !== null && row.backend_balance !== undefined && row.backend_balance !== "";
  const balanceUnavailable = ok && !hasBalance && Boolean(
    row.balance_unavailable || row.permission_limited || row.warning || row.raw?.balance_unavailable || row.raw?.permission_limited
  );
  const balance = hasBalance ? formatCreditBalance(row.backend_balance) : balanceUnavailable ? "Active" : "--";
  const status = ok ? (hasBalance ? "Backend balance" : balanceUnavailable ? "Automation ready" : "Balance unavailable") : "Needs attention";

  return (
    <article className="min-w-0 rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-slate-100">{row.displayName || row.name || gameId}</h3>
          <p className="mt-1 truncate text-xs uppercase tracking-wider text-slate-500">{gameId}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${ok ? "border-emerald-700/30 text-emerald-300" : "border-red-700/30 text-red-300"}`}>
          {ok ? "live" : "error"}
        </span>
      </div>
      <div className="text-3xl font-bold tracking-tight text-white tabular-nums">{balance}</div>
      <div className={`mt-1 text-xs font-semibold uppercase tracking-wider ${ok ? "text-slate-500" : "text-red-300"}`}>
        {status}
      </div>
      {balanceUnavailable && (
        <p className="mt-3 line-clamp-3 rounded-md border border-emerald-500/15 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100/75">
          Load, redeem, account create, and password reset are active. This provider login does not allow agent-balance viewing.
        </p>
      )}
      {!ok && (
        <p className="mt-3 line-clamp-2 text-xs text-red-200">{shortError(row.error)}</p>
      )}
      <button
        type="button"
        disabled={Boolean(busy)}
        onClick={onRefresh}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#4f46e5] px-3 py-2 text-sm font-bold text-white hover:bg-[#5b52f0] disabled:opacity-50"
      >
        <RefreshCw className={`h-4 w-4 ${isBusy ? "animate-spin" : ""}`} />
        {isBusy ? "Refreshing..." : "Refresh balance"}
      </button>
    </article>
  );
}

function Field({ label, children, col = false }) {
  return (
    <label className={col ? "block md:col-span-2" : "block"}>
      <span className="mb-1 block text-xs uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4">
      <Icon className="mb-3 h-5 w-5 text-[#D4AF37]" />
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ActionButton({ id, busy, icon: Icon, onClick, children }) {
  return (
    <button
      type="button"
      disabled={Boolean(busy)}
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-50"
    >
      <Icon className={`h-4 w-4 ${busy === id ? "animate-pulse" : ""}`} />
      {busy === id ? "Working..." : children}
    </button>
  );
}

function ListPanel({ title, rows, empty }) {
  return (
    <section className="rounded-xl border border-white/10 bg-[#121214] p-5">
      <h2 className="mb-4 text-lg font-semibold">{title}</h2>
      <pre className="max-h-80 overflow-auto rounded-lg bg-black/30 p-3 text-xs text-slate-300">{rows.length ? JSON.stringify(rows, null, 2) : empty}</pre>
    </section>
  );
}

function BackendCredentialsPanel({ rows, visible, onCopyAll }) {
  if (!visible) return null;

  return (
    <section className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/10 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-amber-100">Saved agent/backend login list</h2>
          <p className="mt-1 text-sm text-amber-200/80">Admin-only. Hide this panel when you are done copying passwords.</p>
        </div>
        <button
          type="button"
          disabled={!rows.length}
          onClick={onCopyAll}
          className="inline-flex items-center gap-2 rounded-md bg-[#2F6FA5] px-3 py-2 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-50"
        >
          <Copy className="h-4 w-4" />
          Copy all
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-black/20 py-8 text-center text-sm text-amber-100/70">No saved backend logins yet.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((item) => {
            const key = item.gameId || item.id || item.displayName;
            return (
              <article key={key} className="rounded-lg border border-white/10 bg-black/30 p-4 text-sm">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-white">{item.displayName || item.gameId || "Backend"}</h3>
                    <p className="text-xs uppercase tracking-wider text-slate-500">{item.gameId || "-"}</p>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${item.passwordSaved ? "border-emerald-700/30 text-emerald-300" : "border-red-700/30 text-red-300"}`}>
                    {item.passwordSaved ? "password saved" : "no password"}
                  </span>
                </div>
                <CredentialLine label="Backend" value={item.backendUrl || "-"} />
                <CredentialLine label="Agent ID" value={item.username || "-"} />
                <CredentialLine label="Password" value={item.password || "(not saved)"} />
                <CredentialLine label="Token/Cookie" value={item.tokenSaved ? "saved" : "not saved"} />
                {item.updated_at && <p className="mt-3 text-xs text-slate-500">Updated: {item.updated_at}</p>}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CredentialLine({ label, value, onCopy }) {
  const copyValue = async () => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      toast.error(`${label} is empty`);
      return;
    }
    if (onCopy) {
      onCopy();
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="mt-2 rounded-md border border-white/5 bg-black/30 px-3 py-2">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate text-sm text-slate-100">{value}</code>
        <button
          type="button"
          disabled={!value || value === "-"}
          onClick={copyValue}
          className="rounded border border-white/10 p-1.5 text-slate-300 hover:bg-white/5 disabled:opacity-40"
          title={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function ResultPanel({ result }) {
  return (
    <section className="rounded-xl border border-white/10 bg-[#121214] p-5">
      <h2 className="mb-4 text-lg font-semibold">Last result</h2>
      <pre className="max-h-80 overflow-auto rounded-lg bg-black/30 p-3 text-xs text-slate-300">
        {result ? JSON.stringify(result, null, 2) : "No action run yet."}
      </pre>
    </section>
  );
}

function toRows(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.games)) return value.games;
  if (Array.isArray(value?.attachments)) return value.attachments;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (value && typeof value === "object") return [value];
  return [];
}

function formatBackendLoginList(rows = []) {
  return rows.map((item) => [
    `${item.displayName || item.gameId || "Game"} (${item.gameId || "-"})`,
    `Backend URL: ${item.backendUrl || "-"}`,
    `Agent ID: ${item.username || "-"}`,
    `Password: ${item.password || "(not saved)"}`,
    `Token/Cookie: ${item.tokenSaved ? "saved" : "not saved"}`,
  ].join("\n")).join("\n\n");
}

function gameIdFromAutomationRow(row = {}) {
  return String(row.gameId || row.gameName || row.id || row.name || "").trim();
}

function formatCreditBalance(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "--";
  return `$${number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortError(value) {
  if (!value) return "Provider did not return a balance.";
  if (typeof value === "string") return value;
  if (typeof value?.detail === "string") return value.detail;
  if (typeof value?.error === "string") return value.error;
  if (typeof value?.message === "string") return value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return "Provider error";
  }
}

function automationGameId(value = "") {
  const key = String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const map = {
    juwa: "juwa",
    juwa20: "juwa2",
    juwa2: "juwa2",
    riversweeps: "riversweeps",
    vegassweeps: "lasvegassweeps",
    lasvegassweeps: "lasvegassweeps",
    firekirin: "firekirin",
    gamevault: "gamevault",
    pandamaster: "pandamaster",
    orionstars: "orionstars",
    mafia: "mafia",
    cashfrenzy: "cashfrenzy",
    mrallinone: "mrallinone",
    yolo: "yolo",
    cashmachine: "cashmachine",
    vblink: "vblink",
    milkyways: "milkyway",
    milkyway: "milkyway",
    gameroom: "gameroom",
    ultrapanda: "ultrapanda",
    bluedragon: "bluedragon",
    noble777: "noble777",
    vegasroll: "vegasroll",
    winstar: "winstar",
  };
  return map[key] || key;
}

const inputClass = "w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#D4AF37]";
